import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useBestaetigung } from '@pilot-hub/ui'
import { ListenTabelle, type SpalteDef } from '../components/ListenTabelle'
import { usePersistState, useNeuParam, FilterZurueck, KennzahlenLeiste, statusKennzahlen } from '../components/ListenWerkzeuge'
import { FormAktionsleiste, useUngespeichert } from '../components/Formular'
import { ZeilenAktionen } from '../components/ZeilenAktionen'
import { StatusBadge, statusLabel } from '../components/StatusBadge'
import { PageHeader, Card, Button, Input, Select, Textarea } from '@pilot-hub/ui'
import { api, droneApi, akkuApi } from '@pilot-hub/api-client'
import { ArrowLeft, Plus, Edit, Eye, Trash2, FileText, Upload, ExternalLink, Loader2, Save } from 'lucide-react'
import { DrohneWartung } from '../components/DrohneWartung'
import { ZustandBadge } from '../components/AkkuDetail'
import { DatumFeld } from '../components/DatumFelder'

interface Drohne {
  id: number
  asb_inventarnummer: string
  seriennummer: string | null
  dronen_referenz_id: number | null
  hersteller: string | null
  modell: string | null
  typ: string | null
  bezeichnung: string | null
  standort: string | null
  status: string | null
  kennzeichen: string | null
  versicherung_nummer: string | null
  versicherung_pfad: string | null
  bemerkungen: string | null
  erstellt_am: string | null
  akku_anzahl: number
  akku_ids: number[]
  besitzer_typ: string | null
  besitzer_nutzer_id: number | null
  besitzer_label: string | null
  firmware_version?: string | null
  inbetriebnahme?: string | null
  groesse_klasse?: string | null
  eu_klasse?: string | null
  startmasse_g?: number | null
  /** Wartungsverwaltung (nur ASB-Drohnen) */
  verwaltet?: boolean
  zustand?: 'gut' | 'wartung' | 'pruefen' | null
  zustand_gruende?: string[]
}

interface Referenz {
  id: number; hersteller: string | null; modell: string | null; typ: string | null
  gewicht_g?: number | null; groesse_klasse?: string | null; eu_klasse?: string | null; startmasse_g?: number | null; risiko_quelle?: string | null
}
const GROESSE_LABEL: Record<string, string> = { bis1: 'bis 1 m', bis3: 'über 1 m bis unter 3 m', ab3: '3 m oder mehr' }

const LEER = {
  asb_inventarnummer: '', seriennummer: '', hersteller_ref: '', dronen_referenz_id: '',
  besitzer_typ: 'ASB', besitzer_nutzer_id: '',
  standort: '', status: 'VERFUEGBAR', kennzeichen: '', versicherung_nummer: '',
  bemerkungen: '', inbetriebnahme: '', groesse_klasse: '', eu_klasse: '', startmasse_g: ''
}

type ViewMode = 'list' | 'create' | 'edit' | 'detail' | 'akkus'
type ViewState = { mode: ViewMode; drohneId?: number }

export function DronesPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const navigate = useNavigate()
  const [drohnen, setDrohnen] = useState<Drohne[]>([])
  const [referenzen, setReferenzen] = useState<Referenz[]>([])
  /** Nutzer, die als Besitzer einer Drohne gewählt werden dürfen */
  const [besitzerNutzer, setBesitzerNutzer] = useState<{ id: number; vorname?: string | null; nachname?: string | null; benutzername: string }[]>([])
  const [search, setSearch] = usePersistState('drohnen.search', '')
  const [herstellerFilter, setHerstellerFilter] = usePersistState('drohnen.herstellerFilter', 'all')
  const [statusFilter, setStatusFilter] = usePersistState('drohnen.statusFilter', 'all')
  const [standortFilter, setStandortFilter] = usePersistState('drohnen.standortFilter', 'all')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  const [view, setView] = useState<ViewState>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ ...LEER })
  const [speichert, setSpeichert] = useState(false)
  const [showVersicherungUpload, setShowVersicherungUpload] = useState(false)
  const [uploadingVersicherung, setUploadingVersicherung] = useState(false)
  /** Bezeichnungen der Akkus für die Ansicht „Akkus von …“ */
  const [akkuNamen, setAkkuNamen] = useState<Record<number, { bezeichnung: string; seriennummer?: string | null; status?: string | null }>>({})
  useEffect(() => {
    if (view.mode !== 'akkus') return
    akkuApi.list({ size: 500 })
      .then(r => {
        const liste: any[] = (r.data as any).items ?? (r.data as any) ?? []
        setAkkuNamen(Object.fromEntries(liste.map(a => [a.id, a])))
      })
      .catch(() => {})
  }, [view.mode])
  /** Beim Anlegen gewählter Versicherungsschein – wird nach dem Speichern der Drohne hochgeladen */
  const [neueVersicherung, setNeueVersicherung] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileInputKey, setFileInputKey] = useState(0)

  // Versicherungsschein anzeigen: iFrame und neuer Tab senden keinen Authorization-Header (auch iPad/Safari) –
  // daher holt die Seite einen kurzlebigen, signierten Link für genau dieses Dokument (wie bei Lizenzen).
  const [versUrl, setVersUrl] = useState<string | null>(null)
  const [versFehler, setVersFehler] = useState<string | null>(null)
  /** Erhöht nach jedem Upload, damit der neue Inhalt geladen wird */
  const [versStand, setVersStand] = useState(0)
  const versDrohne = view.drohneId ? drohnen.find(d => d.id === view.drohneId) : null
  useEffect(() => {
    setVersUrl(null); setVersFehler(null)
    if (!versDrohne?.id || !versDrohne.versicherung_pfad) return
    let aktiv = true
    api.get(`/drohnen/${versDrohne.id}/versicherung/link`)
      .then(r => { if (aktiv) setVersUrl(`${(r.data as any).url}&v=${versStand}`) })
      .catch((e: any) => { if (aktiv) setVersFehler(e?.response?.data?.detail || 'Versicherungsschein konnte nicht geladen werden') })
    return () => { aktiv = false }
  }, [versDrohne?.id, versDrohne?.versicherung_pfad, versStand])

  const geaendert = useUngespeichert(form, JSON.stringify(view))
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const [d, r] = await Promise.all([
        droneApi.list({ size: 500 }),
        droneApi.getReferenzen()
      ])
      setDrohnen((d.data as any).items ?? [])
      const rData = (r?.data as any) ?? {}; setReferenzen(rData.items ? rData.items : (Array.isArray(rData) ? rData : []))
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Drohnen konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])
  useNeuParam(oeffneNeu, !loading)
  useEffect(() => {
    api.get('/nutzer/besitzer-drohnen').then(r => setBesitzerNutzer(Array.isArray(r.data) ? (r.data as any) : [])).catch(() => {})
  }, [])

  // Direktlink /drones?drohne=<id> (z. B. aus dem Dashboard): Detailansicht öffnen, sobald die Liste geladen ist
  const [suchParameter, setSuchParameter] = useSearchParams()
  useEffect(() => {
    const id = Number(suchParameter.get('drohne'))
    if (!id || loading) return
    const d = drohnen.find(x => x.id === id)
    if (d) oeffneDetail(d)
    setSuchParameter({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suchParameter, loading, drohnen])

  // Auto-set default manufacturer when referenzen load (for faster selection)
  useEffect(() => {
    if (referenzen.length > 0 && !form.hersteller_ref && view.mode === 'create') {
      setForm(prev => ({ ...prev, hersteller_ref: referenzen[0].hersteller || '' }))
    }
  }, [referenzen.length, view.mode])

  function oeffneNeu() {
    setForm({ ...LEER, hersteller_ref: referenzen.length > 0 ? (referenzen[0].hersteller || '') : '' })
    setView({ mode: 'create' })
  }

  function oeffneBearbeiten(d: Drohne) {
    const ref = d.dronen_referenz_id ? referenzen.find(r => r.id === d.dronen_referenz_id) : null
    setForm({
      asb_inventarnummer: d.asb_inventarnummer ?? '',
      seriennummer: d.seriennummer ?? '',
      hersteller_ref: ref?.hersteller || '',
      dronen_referenz_id: d.dronen_referenz_id ? String(d.dronen_referenz_id) : '',
      besitzer_typ: d.besitzer_typ || 'ASB',
      besitzer_nutzer_id: d.besitzer_nutzer_id ? String(d.besitzer_nutzer_id) : '',
      standort: d.standort ?? '',
      status: d.status ?? 'VERFUEGBAR',
      kennzeichen: d.kennzeichen ?? '',
      versicherung_nummer: d.versicherung_nummer ?? '',
      bemerkungen: d.bemerkungen ?? '',
      inbetriebnahme: d.inbetriebnahme ?? '',
      groesse_klasse: d.groesse_klasse ?? '',
      eu_klasse: d.eu_klasse ?? '',
      startmasse_g: d.startmasse_g != null ? String(d.startmasse_g) : ''
    })
    setView({ mode: 'edit', drohneId: d.id })
  }

  function oeffneDetail(d: Drohne) {
    setView({ mode: 'detail', drohneId: d.id })
    setForm({
      asb_inventarnummer: d.asb_inventarnummer,
      seriennummer: d.seriennummer || '',
      dronen_referenz_id: String(d.dronen_referenz_id ?? ''),
      besitzer_typ: d.besitzer_typ || 'ASB',
      besitzer_nutzer_id: d.besitzer_nutzer_id ? String(d.besitzer_nutzer_id) : '',
      standort: d.standort || '',
      status: d.status || '',
      kennzeichen: d.kennzeichen || '',
      versicherung_nummer: d.versicherung_nummer || '',
      bemerkungen: d.bemerkungen || '',
      inbetriebnahme: d.inbetriebnahme || '',
      groesse_klasse: d.groesse_klasse || '',
      eu_klasse: d.eu_klasse || '',
      startmasse_g: d.startmasse_g != null ? String(d.startmasse_g) : '',
    })
  }

  function oeffneAkkus(d: Drohne) {
    setView({ mode: 'akkus', drohneId: d.id })
  }

  function zurueckZurListe() {
    setNeueVersicherung(null)
    setView({ mode: 'list' })
    setForm({ ...LEER })
  }

  async function speichern() {
    if (!form.asb_inventarnummer) return
    if (form.besitzer_typ === 'NUTZER' && !form.besitzer_nutzer_id) { setFehler('Bitte einen Nutzer als Besitzer wählen'); return }
    setSpeichert(true); setFehler(null)
    try {
      const payload: any = {
        asb_inventarnummer: form.asb_inventarnummer,
        seriennummer: form.seriennummer || null,
        dronen_referenz_id: form.dronen_referenz_id ? Number(form.dronen_referenz_id) : null,
        besitzer_typ: form.besitzer_typ || 'ASB',
        besitzer_nutzer_id: form.besitzer_typ === 'NUTZER' && form.besitzer_nutzer_id ? Number(form.besitzer_nutzer_id) : null,
        standort: form.standort || null,
        status: form.status,
        kennzeichen: form.kennzeichen || null,
        versicherung_nummer: form.versicherung_nummer || null,
        bemerkungen: form.bemerkungen || null,
        inbetriebnahme: form.inbetriebnahme || null,
        groesse_klasse: form.groesse_klasse || null,
        eu_klasse: form.eu_klasse || null,
        startmasse_g: form.startmasse_g ? Number(form.startmasse_g) : null
      }
      if (view.mode === 'edit' && view.drohneId) await droneApi.update(view.drohneId, payload)
      else {
        const neu = await droneApi.create(payload)
        const neueId = (neu.data as any)?.id
        if (neueVersicherung && neueId) await ladeVersicherungHoch(neueVersicherung, neueId)
      }
      zurueckZurListe()
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally {
      setSpeichert(false)
    }
  }

  async function loeschen(id: number) {
    try {
      await droneApi.delete(id)
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Löschen fehlgeschlagen')
    }
  }

  const triggerFileInput = (ref: React.RefObject<HTMLInputElement>) => {
    ref.current?.click()
  }

  const getToken = () => {
    const authKey = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
    const authData = localStorage.getItem(authKey)
    if (authData) {
      try {
        const parsed = JSON.parse(authData)
        return parsed.accessToken || parsed.access_token || parsed.state?.accessToken || parsed.state?.access_token
      } catch {}
    }
    return localStorage.getItem('access_token')
  }

  function triggerVersicherungUpload() {
    triggerFileInput(fileInputRef)
  }

  async function uploadVersicherung(file: File) {
    setFileInputKey(k => k + 1)
    if (!(file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))) {
      setFehler('Nur PDF-Dateien erlaubt (Datei muss .pdf Endung haben)')
      return
    }
    // Neue Drohne: Datei merken und nach dem Speichern hochladen
    if (!currentDrohne) { setFehler(null); setNeueVersicherung(file); return }
    await ladeVersicherungHoch(file, currentDrohne.id)
  }

  async function ladeVersicherungHoch(file: File, drohneId: number) {
    if (!getToken()) {
      setFehler('Kein Zugriffstoken gefunden. Bitte neu anmelden.')
      return
    }
    setUploadingVersicherung(true)
    try {
      // Use chunked upload for files > 500KB
      if (file.size > 500 * 1024) {
        await uploadVersicherungChunked(file, drohneId)
      } else {
        await uploadVersicherungDirect(file, drohneId)
      }
      
      // Refresh current drohne from list
      await laden()
      setVersStand(n => n + 1)
    } catch (e: any) {
      setFehler(e?.message || 'Upload fehlgeschlagen')
    } finally {
      setUploadingVersicherung(false)
    }
  }

  async function uploadVersicherungDirect(file: File, drohneId: number) {
    
    const token = getToken()
    if (!token) throw new Error('Kein Zugriffstoken gefunden.')
    
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`/api/v1/drohnen/${drohneId}/versicherung`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.detail || `Upload fehlgeschlagen (${response.status})`)
    }
  }

  async function uploadVersicherungChunked(file: File, drohneId: number) {
    
    const token = getToken()
    if (!token) throw new Error('Kein Zugriffstoken gefunden.')
    
    const CHUNK_SIZE = 512 * 1024
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
    
    // 1. Initialize upload
    const initResponse = await fetch(`/api/v1/drohnen/${drohneId}/versicherung/init`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ filename: file.name, total_chunks: totalChunks })
    })
    
    if (!initResponse.ok) {
      const error = await initResponse.json()
      throw new Error(error.detail || 'Initialisierung fehlgeschlagen')
    }
    
    const { upload_id } = await initResponse.json()
    
    // 2. Upload chunks sequentially
    for (let i = 0; i < totalChunks; i++) {
      const start = i * 512 * 1024
      const end = Math.min(start + 512 * 1024, file.size)
      const chunk = file.slice(start, end)
      
      const chunkFormData = new FormData()
      chunkFormData.append('upload_id', upload_id)
      chunkFormData.append('chunk_index', String(i))
      chunkFormData.append('file', chunk, `${file.name}.part${i}`)
      
      const chunkResponse = await fetch(`/api/v1/drohnen/${drohneId}/versicherung/chunk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: chunkFormData
      })
      
      if (!chunkResponse.ok) {
        const error = await chunkResponse.json()
        throw new Error(`Chunk ${i + 1}/${totalChunks} fehlgeschlagen: ${error.detail || 'Unbekannter Fehler'}`)
      }
    }
    
    // 3. Complete upload
    const completeResponse = await fetch(`/api/v1/drohnen/${drohneId}/versicherung/complete`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ upload_id })
    })
    
    if (!completeResponse.ok) {
      const error = await completeResponse.json()
      throw new Error(error.detail || 'Abschluss fehlgeschlagen')
    }
  }

  const uniq = (xs: (string | null | undefined)[]) => Array.from(new Set(xs.filter((x): x is string => !!x))).sort((a, b) => a.localeCompare(b, 'de'))
  const herstellerListe = uniq(drohnen.map(d => d.hersteller))
  const statusListe = uniq(drohnen.map(d => d.status))
  const standortListe = uniq(drohnen.map(d => d.standort))
  const gefiltert = drohnen.filter(d =>
    (herstellerFilter === 'all' || d.hersteller === herstellerFilter) &&
    (statusFilter === 'all' || d.status === statusFilter) &&
    (standortFilter === 'all' || d.standort === standortFilter) && (
    (d.asb_inventarnummer ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (d.seriennummer ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (d.bezeichnung ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (d.modell ?? '').toLowerCase().includes(search.toLowerCase()))
  )

  const columns: SpalteDef[] = [
    { key: 'inventar', header: 'Inventarnummer', accessor: 'asb_inventarnummer', render: (_v: any, d: any) => <span className="font-medium">{d.asb_inventarnummer}</span> },
    { key: 'modell', header: 'Modell', accessor: 'modell', sortWert: (d: any) => d.bezeichnung || d.modell, render: (_v: any, d: any) => d.bezeichnung || d.modell || '—' },
    { key: 'sn', header: 'Seriennummer', accessor: 'seriennummer', render: (_v: any, d: any) => <span className="font-mono text-xs">{d.seriennummer || '—'}</span> },
    { key: 'standort', header: 'Standort', accessor: 'standort', render: (_v: any, d: any) => d.standort || '—' },
    { key: 'status', header: 'Status', accessor: 'status', sortWert: (d: any) => statusLabel(d.status), render: (_v: any, d: any) => <StatusBadge wert={d.status} /> },
    { key: 'zustand', header: 'Wartung', accessor: 'zustand', render: (_v: any, d: any) =>
      d.verwaltet && d.zustand ? <ZustandBadge zustand={d.zustand} gruende={d.zustand_gruende ?? []} /> : <span className="text-xs text-dark-text-subtle">—</span> },
    { key: 'besitzer', header: 'Besitzer', accessor: 'id', sortWert: (d: any) => d.besitzer_label || 'ASB-Drohnenstaffel', render: (_v: any, d: any) => {
      if (d.besitzer_label) return <span className="text-dark-text">{d.besitzer_label}</span>
      if (!d.besitzer_typ || d.besitzer_typ === 'ASB') return <span className="text-dark-text">ASB-Drohnenstaffel</span>
      if (d.besitzer_nutzer_id) return <span className="text-dark-text">Nutzer #{d.besitzer_nutzer_id}</span>
      return <span className="text-dark-text-subtle">—</span>
    }},
    { key: 'akkus', header: 'Akkus', accessor: 'akku_anzahl', mobile: 'hidden', render: (_v: any, d: any) => (
      <button onClick={() => oeffneAkkus(d)} className="text-primary-400 hover:text-primary-300 underline underline-offset-2">
        {d.akku_anzahl} Akku(s)
      </button>
    )},
    {
      key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, row: any) => (
        <ZeilenAktionen name={String([row.asb_inventarnummer, row.bezeichnung || row.modell].filter(Boolean).join(' '))} onBearbeiten={() => oeffneBearbeiten(row)} eintraege={[{ label: 'Flugbuch exportieren', icon: <FileText className="h-4 w-4" />, onClick: () => navigate(`/export?art=drohne&id=${row.id}`) }]} onLoeschen={async () => { const name = [row.asb_inventarnummer, row.bezeichnung || row.modell].filter(Boolean).join(' '); if (await bestaetigen({ titel: 'Drohne löschen?', text: 'Drohne', objekt: String(name), hinweis: 'Die Akku-Zuordnungen werden entfernt. Das kann nicht rückgängig gemacht werden.' })) droneApi.delete(row.id).then(laden) }} />
      )
    },
  ]

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        <PageHeader
          title=""
          subtitle="Drohnenbestand — Modell aus der Basis-Datenbank wählen, Rest wird übernommen"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neue Drohne</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        <Card>
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-start">
            <Input placeholder="Inventarnr., Seriennr., Modell" value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-80" />
            <FilterZurueck aktiv={!!search || herstellerFilter !== 'all' || statusFilter !== 'all' || standortFilter !== 'all'} onClick={() => { setSearch(''); setHerstellerFilter('all'); setStatusFilter('all'); setStandortFilter('all') }} />
            <div className="text-sm text-dark-text-muted whitespace-nowrap sm:ml-auto">{gefiltert.length} von {drohnen.length}</div>
          </div>
          <div className="mb-4"><KennzahlenLeiste items={statusKennzahlen(drohnen.map(d => d.status), statusFilter, setStatusFilter, 'Alle Drohnen')} /></div>
          <div className="mb-4 grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Select value={herstellerFilter} onChange={e => setHerstellerFilter(e.target.value)} placeholder="Hersteller"
              options={[{ value: 'all', label: 'Alle Hersteller' }, ...herstellerListe.map(h => ({ value: h, label: h }))]} />
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} placeholder="Status"
              options={[{ value: 'all', label: 'Alle Status' }, ...statusListe.map(h => ({ value: h, label: statusLabel(h) }))]} />
            <Select value={standortFilter} onChange={e => setStandortFilter(e.target.value)} placeholder="Standort"
              options={[{ value: 'all', label: 'Alle Standorte' }, ...standortListe.map(h => ({ value: h, label: h }))]} />
          </div>

          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Drohnen…</div> : (
            <ListenTabelle persistKey="drohnen" data={gefiltert} columns={columns} keyAccessor={(d: any) => String(d.id)} onRowClick={oeffneDetail} emptyMessage="Keine Drohnen vorhanden" />
          )}
        </Card>
        {dialog}
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT / AKKUS VIEW ======
  const currentDrohne = view.drohneId ? drohnen.find(d => d.id === view.drohneId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'
  const isAkkus = view.mode === 'akkus'

  if (isAkkus && currentDrohne) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={`Akkus von ${currentDrohne.asb_inventarnummer}`}
          subtitle="Zugeordnete Akkus"
          action={<Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>}
        />
        <Card>
          {currentDrohne.akku_ids.length === 0 ? (
            <p className="text-dark-text-muted py-8 text-center">Keine Akkus zugeordnet</p>
          ) : (
            <ul className="space-y-2 max-h-96 overflow-y-auto">
              {currentDrohne.akku_ids.map((akkuId: number) => (
                <li key={akkuId} className="text-sm flex items-center justify-between p-3 bg-dark-bg-tertiary rounded-lg">
                  <span>{akkuNamen[akkuId]?.bezeichnung || `Akku #${akkuId}`}
                    {akkuNamen[akkuId]?.seriennummer && <span className="ml-2 text-dark-text-muted">SN {akkuNamen[akkuId]?.seriennummer}</span>}
                  </span>
                  {akkuNamen[akkuId]?.status && <StatusBadge wert={akkuNamen[akkuId]!.status} />}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isCreate ? 'Neue Drohne' : isEdit ? 'Drohne bearbeiten' : 'Drohne anzeigen'}
        subtitle={isCreate ? 'Neue Drohne anlegen' : currentDrohne ? currentDrohne.asb_inventarnummer : ''}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentDrohne && <Button onClick={() => oeffneBearbeiten(currentDrohne)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
          </div>
        }
      />

      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

      <Card>
        <div className="space-y-4">
          <div>
            <label className="label">Inventarnummer</label>
            <Input value={form.asb_inventarnummer} onChange={e => setForm({...form, asb_inventarnummer: e.target.value})} disabled={isDetail} placeholder="z.B. Einsatzdrohne 01" />
          </div>
          <div>
            <label className="label">Seriennummer</label>
            <Input value={form.seriennummer} onChange={e => setForm({...form, seriennummer: e.target.value})} disabled={isDetail} placeholder="z.B. DJI-12345" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Hersteller</label>
              <Select value={form.hersteller_ref || ''} onChange={e => setForm({...form, hersteller_ref: e.target.value, dronen_referenz_id: ''})} disabled={isDetail}
                options={Array.from(new Set(referenzen.map(r => r.hersteller).filter((h): h is string => !!h))).map(h => ({ value: h, label: h }))}
                placeholder="— Hersteller wählen —" />
            </div>
            <div>
              <label className="label">Modell</label>
              <Select value={form.dronen_referenz_id} onChange={e => setForm({...form, dronen_referenz_id: e.target.value})} disabled={isDetail}
                options={referenzen.filter(r => !form.hersteller_ref || r.hersteller === form.hersteller_ref).map(r => ({ value: String(r.id), label: `${r.hersteller} ${r.modell} (${r.typ})` }))}
                placeholder="— Modell wählen —" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Besitzer</label>
              <Select value={form.besitzer_typ || 'ASB'} onChange={e => setForm({...form, besitzer_typ: e.target.value, besitzer_nutzer_id: ''})} disabled={isDetail}
                options={[{ value: 'ASB', label: 'ASB-Drohnenstaffel' }, { value: 'NUTZER', label: 'Privat (Nutzer)' }]} />
            </div>
            {form.besitzer_typ === 'NUTZER' && (
              <div>
                <label className="label">Nutzer</label>
                <Select value={form.besitzer_nutzer_id || ''} onChange={e => setForm({...form, besitzer_nutzer_id: e.target.value})} disabled={isDetail}
                  options={besitzerNutzer.map(n => ({ value: String(n.id), label: `${`${n.vorname || ''} ${n.nachname || ''}`.trim() || n.benutzername} (${n.benutzername})` }))}
                  placeholder="— Nutzer wählen —" />
              </div>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Standort</label>
              <Input value={form.standort} onChange={e => setForm({...form, standort: e.target.value})} disabled={isDetail} placeholder="z.B. Else Stein Halle" />
            </div>
            <div>
              <label className="label">Status</label>
              <Select value={form.status} onChange={e => setForm({...form, status: e.target.value})} disabled={isDetail}
                options={['VERFUEGBAR', 'EINSATZBEREIT', 'IM_EINSATZ', 'WARTUNG', 'DEFEKT', 'AUSSER_BETRIEB'].map(s => ({ value: s, label: s }))} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Inbetriebnahme</label>
              <DatumFeld  value={form.inbetriebnahme} onChange={v => setForm({...form, inbetriebnahme: v})} disabled={isDetail} />
            </div>
            <div className="flex items-end text-xs text-dark-text-subtle">Basis für Wartungsintervalle in Monaten, solange noch keine Wartung erfasst ist.</div>
          </div>
          <div className="rounded-lg border border-dark-border p-3">
            <div className="mb-2 text-sm font-medium text-dark-text">Angaben für die Risikobewertung (EGRED 2)</div>
            <p className="mb-3 text-xs text-dark-text-subtle">Werden in der Risikobewertung im Führungs-Modul automatisch übernommen. Leer = Vorgabe aus dem Drohnenkatalog; fehlt auch die, bleibt das Feld dort frei wählbar.</p>
            {(() => {
              const ref = referenzen.find(x => String(x.id) === String(form.dronen_referenz_id))
              if (!ref || !(ref.groesse_klasse || ref.eu_klasse || ref.startmasse_g || ref.gewicht_g)) return null
              return (
                <div className="mb-3 rounded-md bg-dark-bg-tertiary/50 px-3 py-2 text-xs text-dark-text-muted">
                  <span className="font-medium">Katalog {ref.hersteller} {ref.modell}:</span>{' '}
                  {[ref.groesse_klasse && GROESSE_LABEL[ref.groesse_klasse], ref.eu_klasse && (ref.eu_klasse === 'keine' ? 'keine EU-Klasse' : `EU-Klasse ${ref.eu_klasse}`),
                    (ref.startmasse_g || ref.gewicht_g) && `${(ref.startmasse_g || ref.gewicht_g)!.toLocaleString('de-DE')} g`].filter(Boolean).join(' · ')}
                  {ref.risiko_quelle && <div className="mt-1 text-dark-text-subtle">{ref.risiko_quelle}</div>}
                </div>
              )
            })()}
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="label">Charakteristische Dimension</label>
                <Select value={form.groesse_klasse} onChange={e => setForm({...form, groesse_klasse: e.target.value})} disabled={isDetail}
                  options={[{ value: '', label: '-- Aus Katalog --' }, { value: 'bis1', label: 'bis 1 m' }, { value: 'bis3', label: 'über 1 m bis unter 3 m' }, { value: 'ab3', label: '3 m oder mehr' }]} />
                <p className="mt-1 text-xs text-dark-text-subtle">Diagonal von Rotorspitze zu Rotorspitze.</p>
              </div>
              <div>
                <label className="label">EU-Klasse</label>
                <Select value={form.eu_klasse} onChange={e => setForm({...form, eu_klasse: e.target.value})} disabled={isDetail}
                  options={[{ value: '', label: '-- Aus Katalog --' }, ...['C0', 'C1', 'C2', 'C3', 'C4', 'C5', 'C6'].map(k => ({ value: k, label: k })), { value: 'keine', label: 'Keine (Bestandsdrohne)' }]} />
              </div>
              <div>
                <label className="label">Max. Startmasse (g)</label>
                <Input type="number" min={0} value={form.startmasse_g} onChange={e => setForm({...form, startmasse_g: e.target.value})} disabled={isDetail} placeholder="leer = aus Drohnenkatalog" />
              </div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Kennzeichen</label>
              <Input value={form.kennzeichen} onChange={e => setForm({...form, kennzeichen: e.target.value})} disabled={isDetail} placeholder="z.B. DA-001" />
            </div>
            <div>
              <label className="label">Versicherungsnummer</label>
              <Input value={form.versicherung_nummer} onChange={e => setForm({...form, versicherung_nummer: e.target.value})} disabled={isDetail} placeholder="z.B. V-1001" />
            </div>
          </div>
          <div>
            <label className="label">Bemerkungen</label>
            <Textarea value={form.bemerkungen} onChange={e => setForm({...form, bemerkungen: e.target.value})} disabled={isDetail} rows={3} />
          </div>

          {/* Versicherungsschein PDF Upload - auch für Create/Edit */}
          {!isDetail && (
            <div className="pt-4 border-t border-dark-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-dark-text flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary-400" /> Versicherungsschein (PDF)
                </h4>
              </div>
              <div className="p-6 text-center border-2 border-dashed border-dark-border rounded-lg bg-dark-bg-tertiary/50">
                <FileText className="mx-auto mb-2 h-10 w-10 text-dark-text-muted" />
                <p className="text-dark-text-muted mb-4">
                  {uploadingVersicherung ? 'Wird hochgeladen …'
                    : neueVersicherung ? <>Ausgewählt: <span className="text-dark-text">{neueVersicherung.name}</span> – wird beim Speichern hochgeladen</>
                    : currentDrohne?.versicherung_pfad ? 'Versicherungsschein ist hinterlegt – hier ersetzen'
                    : 'Kein Versicherungsschein hochgeladen'}
                </p>
                <div className="flex flex-col items-center gap-3">
                  <div className="relative w-full max-w-xs">
                    <button 
                      type="button"
                      onClick={triggerVersicherungUpload}
                      className="flex items-center gap-2 w-full px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors cursor-pointer"
                    >
                      <Upload className="h-4 w-4" />
                      Versicherungsschein hochladen
                    </button>
                    <input 
                      ref={fileInputRef}
                      key={fileInputKey}
                      type="file" 
                      accept=".pdf" 
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVersicherung(f) }} 
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {isDetail && currentDrohne && (
            <>
              <div className="pt-4 border-t border-dark-border">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-dark-text-muted">Hersteller:</span> <span>{currentDrohne.hersteller || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Modell:</span> <span>{currentDrohne.modell || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Typ:</span> <span>{currentDrohne.typ || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Akkus:</span> <span>{currentDrohne.akku_anzahl} Akku(s)</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Besitzer:</span> <span>{currentDrohne.besitzer_label || (currentDrohne.besitzer_typ === 'ASB' ? 'ASB-Drohnenstaffel' : 'Nutzer #' + currentDrohne.besitzer_nutzer_id)}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Erstellt am:</span> <span>{currentDrohne.erstellt_am ? new Date(currentDrohne.erstellt_am).toLocaleDateString('de-DE') : '—'}</span></div>
                </dl>
              </div>
              
              {/* Versicherung PDF */}
              <div className="pt-4 border-t border-dark-border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-dark-text flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary-400" /> Versicherungsschein (PDF)
                  </h4>
                  {currentDrohne.versicherung_pfad && (
                    <a
                      href={versUrl ?? undefined}
                      target="_blank"
                      rel="noopener"
                      aria-disabled={!versUrl}
                      className={`flex items-center gap-1 text-sm transition-colors ${versUrl ? 'text-primary-400 hover:text-primary-300' : 'pointer-events-none text-dark-text-subtle'}`}
                      title="PDF in neuem Tab öffnen"
                    >
                      <ExternalLink className="h-4 w-4" />
                      PDF öffnen
                    </a>
                  )}
                </div>
                {currentDrohne.versicherung_pfad ? (
                  <>
                    <div className="mb-3">
                      {versUrl ? (
                        <iframe src={versUrl} className="w-full h-[300px] rounded-lg border border-dark-border bg-white" title="Versicherungsschein" />
                      ) : (
                        <div className="flex h-[120px] items-center justify-center rounded-lg border border-dark-border text-sm text-dark-text-muted">
                          {versFehler || 'Lade Versicherungsschein …'}
                        </div>
                      )}
                      <p className="mt-1 text-xs text-dark-text-subtle">Auf iPad/iPhone zeigt die Vorschau nur die erste Seite – „PDF öffnen“ zeigt das ganze Dokument.</p>
                    </div>
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative w-full max-w-xs">
                        <button 
                          type="button"
                          onClick={triggerVersicherungUpload}
                          className="flex items-center gap-2 w-full px-4 py-2 rounded-lg border border-primary-500/60 bg-primary-600/15 text-primary-100 font-medium hover:bg-primary-600/25 transition-colors cursor-pointer"
                        >
                          <Upload className="h-4 w-4" />
                          Versicherungsschein ersetzen
                        </button>
                        <input 
                          ref={fileInputRef}
                          key={fileInputKey}
                          type="file" 
                          accept=".pdf" 
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVersicherung(f) }} 
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-6 text-center border-2 border-dashed border-dark-border rounded-lg bg-dark-bg-tertiary/50">
                    <FileText className="mx-auto mb-2 h-10 w-10 text-dark-text-muted" />
                    <p className="text-dark-text-muted mb-4">Kein Versicherungsschein hochgeladen</p>
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative w-full max-w-xs">
                        <button 
                          type="button"
                          onClick={triggerVersicherungUpload}
                          className="flex items-center gap-2 w-full px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors cursor-pointer"
                        >
                          <Upload className="h-4 w-4" />
                          Versicherungsschein hochladen
                        </button>
                        <input 
                          ref={fileInputRef}
                          key={fileInputKey}
                          type="file" 
                          accept=".pdf" 
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVersicherung(f) }} 
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Card>

      {isDetail && currentDrohne && (
        <DrohneWartung drohneId={currentDrohne.id} referenzId={currentDrohne.dronen_referenz_id}
          modellName={currentDrohne.bezeichnung} onGeaendert={laden} />
      )}
      {!isDetail && <FormAktionsleiste geaendert={geaendert} speichert={speichert} speichernGesperrt={!form.asb_inventarnummer} onSpeichern={speichern} onAbbrechen={zurueckZurListe} />}
    </div>
  )
}